/**
 * ExtranetLayout — Fase 4 Extranet KB
 * Layout do portal externo: header com branding do tenant + sidebar simples.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.1.0 - Bloqueia acesso quando o tenant tem
 *            knowledge_enabled = false (preparação add-on Wiki).
 */
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { useExtranetAuth } from '@/hooks/useExtranetAuth';
import { useKbPublicModuleEnabled } from '@/hooks/kb/useKbPublicModuleEnabled';
import { Button } from '@/components/ui/button';
import { BookOpen, LogOut, User as UserIcon, LayoutGrid, Lock, ShieldCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export function ExtranetLayout() {
  const { tenant, externalUser, signOut } = useExtranetAuth();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = `/extranet/${tenantSlug}`;

  // Gate: módulo Base de Conhecimento pode estar desativado (add-on inativo).
  const { data: moduleEnabled = true } = useKbPublicModuleEnabled(tenantSlug);

  const initials = (externalUser?.display_name || externalUser?.email || 'U')
    .split(' ')
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  if (!moduleEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full rounded-lg border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Lock className="h-6 w-6 text-muted-foreground" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">Portal indisponível</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            O portal do cliente está temporariamente inacessível. Tente novamente em breve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={base} className="flex items-center gap-2 flex-1 min-w-0">
            {tenant?.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={tenant.name}
                className="h-9 w-9 rounded object-contain bg-muted"
              />
            ) : (
              <div className="h-9 w-9 rounded bg-primary/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                {tenant?.name ?? 'Base de Conhecimento'}
              </div>
              <div className="text-[11px] text-muted-foreground">Portal do Cliente</div>
            </div>
          </Link>

          <NavLink to={`${base}/perfil`} className="hidden sm:block">
            <Avatar className="h-9 w-9">
              <AvatarImage src={undefined} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          </NavLink>

          <Button variant="ghost" size="sm" onClick={signOut} className="gap-2">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sair</span>
          </Button>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1">
          <NavItem to={base} end icon={<LayoutGrid className="h-4 w-4" />}>
            Espaços
          </NavItem>
          <NavItem to={`${base}/meus-acessos`} icon={<ShieldCheck className="h-4 w-4" />}>
            Meus acessos
          </NavItem>
          <NavItem to={`${base}/perfil`} icon={<UserIcon className="h-4 w-4" />}>
            Meu perfil
          </NavItem>
        </aside>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({
  to,
  end,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
